import { useState } from 'react';

/**
 * A custom hook that preserves modal content during animations by freezing it when the modal opens.
 * This prevents content flashing during closing animations.
 * 
 * @template T - The type of content to freeze
 * @param content - The content that would normally be displayed in the modal
 * @param isOpen - Boolean indicating if the modal is open
 * @returns The frozen content (preserves original type T)
 */
export function useModalContent<T>(content: T, isOpen: boolean): T {
  const [frozenContent, setFrozenContent] = useState<T>(content);
  const [prevIsOpen, setPrevIsOpen] = useState<boolean>(isOpen);

  // Only update content when modal is opening, not when closing
  if (isOpen && !prevIsOpen) {
    setFrozenContent(content);
  }
  
  // Track previous open state
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
  }

  return frozenContent;
}

export default useModalContent;
